import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ARichTextReadOnly from "components/ARichTextReadOnly/ARichTextReadOnly";
import ClickableText from "components/ClickableText/ClickableText";
import { PromptDialog } from "components/PromptDialog";
import { formatDateTime } from "helpers";
import { CommentType } from "operations/schema/schema";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

type CommentProps = {
  commentIn: CommentType;
  handleDelete?: (commentId: number) => void;
  setEditMode?: (comment: CommentType) => void;
};

export const Comment: FC<CommentProps> = (props) => {
  const { commentIn, handleDelete, setEditMode } = props;
  const intl = useIntl();
  const [comment, setComment] = useState<CommentType | null>(null);
  const [commentHeight, setCommentHeight] = useState<number>(130);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);

  useEffect(() => {
    if (commentIn) {
      const numParagraphs = commentIn.text?.match(new RegExp("<\\/.{1,2}>", "g"))?.length ?? 0;
      const textToCheckLength = commentIn.text?.replace(new RegExp("<span[^>]*>", "g"), "");
      if (numParagraphs > 4) setCommentHeight(180);
      else if (numParagraphs > 3) setCommentHeight(150);
      else if ((textToCheckLength?.length ?? 0) < 80 && numParagraphs < 2) setCommentHeight(70);
      else if ((textToCheckLength?.length ?? 0) < 200 && numParagraphs < 3) setCommentHeight(100);
      else setCommentHeight(130);

      setComment(commentIn);
    }
  }, [commentIn]);

  return (
    <div
      style={{
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        padding: "16px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "1em",
        position: "relative",
        textAlign: "left",
      }}
    >
      <div
        style={{
          height: !comment?.fullAccess ? commentHeight - 25 : commentHeight,
        }}
      >
        {comment !== null && (
          <div style={{ height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <AccountCircleIcon
                sx={{
                  flex: "0 0 auto",
                  color: "primary.main",
                  width: "24px",
                  height: "24px",
                }}
                style={{ marginLeft: -15, marginRight: 5 }}
              />
              <div style={{ width: "60%" }}>
                <b>{comment?.ownerFullName}</b>
              </div>
              <div style={{ width: "40%", textAlign: "end", fontSize: "small" }}>
                {formatDateTime(comment?.changedDate ?? comment.createdDate)}
              </div>
            </div>
            {!comment?.fullAccess && (
              <ARichTextReadOnly content={comment?.text ?? ""} className={"mini"} noBorder={true} />
            )}
            {comment?.fullAccess && (setEditMode || handleDelete) && (
              <div style={{ height: "100%" }}>
                <div style={{ height: "calc(100% - 25px)" }}>
                  <ARichTextReadOnly
                    content={comment?.text ?? ""}
                    className={"mini"}
                    noBorder={true}
                  />
                </div>
                <div>
                  {setEditMode && (
                    <ClickableText
                      style={{ fontSize: "small" }}
                      onClick={() => {
                        setEditMode(commentIn);
                      }}
                    >
                      <FormattedMessage id="comments.edit" />
                    </ClickableText>
                  )}
                  {handleDelete && (
                    <>
                      <ClickableText
                        style={{ fontSize: "small" }}
                        onClick={() => setShowDeleteConfirmation(true)}
                      >
                        <FormattedMessage id="comments.delete" />
                      </ClickableText>
                      <PromptDialog
                        open={showDeleteConfirmation}
                        setOpen={setShowDeleteConfirmation}
                        okText={intl.formatMessage({ id: "general.confirm" })}
                        onOk={() => {
                          setShowDeleteConfirmation(false);
                          handleDelete(comment?.commentId ?? 0);
                        }}
                        title={<FormattedMessage id="comments.deleteComment" />}
                        promptContent={<FormattedMessage id="comments.deletePrompt" />}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
